import React, { useEffect, useState, lazy, Suspense } from 'react';
import './App.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Menu, X, ChevronDown, Star, ArrowRight, CheckCircle, Users, Award, Globe, TrendingUp, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, Send, Package, Shield, Clock, Zap, Search, Filter, Download, Eye, Square, Wrench, Ruler, Layers, RefreshCw, CircleDot, Box, Circle, Hexagon, Grid, BarChart3, MessageSquare, Target, Lightbulb, Handshake, FileCheck, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import StickyContactBar from './components/StickyContactBar';
import FloatingQuoteButton from './components/FloatingQuoteButton';
import WhatsAppButton from './components/WhatsAppButton';
import QuickQuoteModal from './components/QuickQuoteModal';

// Lazy load heavy components
const RFQSystem = lazy(() => import('./components/RFQSystem'));
const TrustBadges = lazy(() => import('./components/TrustBadges'));
const FAQSection = lazy(() => import('./components/FAQSection'));
const CustomCursor = lazy(() => import('./components/CustomCursor'));
const MagneticButton = lazy(() => import('./components/MagneticButton'));
const SocialProofFeed = lazy(() => import('./components/SocialProofFeed'));
const ExitIntentModal = lazy(() => import('./components/ExitIntentModal'));
const TrustSignals = lazy(() => import('./components/TrustSignals'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const CaseStudies = lazy(() => import('./components/CaseStudies'));
const ResponseBadge = lazy(() => import('./components/ResponseBadge'));
const ZohoSalesIQ = lazy(() => import('./components/ZohoSalesIQ'));
const TechnicalLibrary = lazy(() => import('./components/TechnicalLibrary'));
const IndustryNews = lazy(() => import('./components/IndustryNews'));
const UltimateAnalyticsDashboard = lazy(() => import('./components/UltimateAnalyticsDashboard'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WORDMARK_LOGO = "https://customer-assets.emergentagent.com/job_valor-transform/artifacts/1m33joav_Valor%20Branding%20Kit%20Without%20Stripes-01.png";

// Product Categories
const PRODUCT_CATEGORIES = [
  {
    id: 'flats',
    name: 'Copper Flats & Bus Bars',
    description: 'High-conductivity copper flats and bus bars for electrical applications',
    icon: Square,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/xpwwpkct_Busbarr.jpg',
    variants: '50+ Size Variants',
    applications: ['Electrical transformers', 'Distribution panels', 'Power systems'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'ASTM B187, IS 613',
      sizes: '15mm to 200mm width × 3mm to 12mm thickness',
      stock: '✓ In Stock',
      leadTime: '3-5 days'
    }
  },
  {
    id: 'rods',
    name: 'Copper Rods & Wires',
    description: 'Premium quality copper rods and wires for cable and electrical industries',
    icon: Wrench,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/a7j2hm18_images.jpeg',
    variants: '15+ Diameters',
    applications: ['Cable manufacturing', 'Electrical wiring', 'Grounding'],
    specs: {
      purity: '99.95% (OF Grade)',
      standards: 'ASTM B49, B1, IS 1897',
      sizes: '1.6mm to 12mm diameter',
      stock: '✓ In Stock',
      leadTime: '5-7 days'
    }
  },
  {
    id: 'strips',
    name: 'Copper Strips',
    description: 'Precision-cut copper strips for specialized applications',
    icon: Ruler,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/a12jgds2_BusBar.jpg',
    variants: 'Custom Sizes',
    applications: ['Electrical contacts', 'Flexible circuits', 'Shielding'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'ASTM B152, IS 191',
      sizes: '10mm to 100mm width × 0.1mm to 3mm thickness',
      stock: 'Made to Order',
      leadTime: '7-10 days'
    }
  },
  {
    id: 'sheets',
    name: 'Copper Sheets',
    description: 'High-quality copper sheets for manufacturing and fabrication',
    icon: Layers,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/fxkwz8g7_Cathode.jpg',
    variants: 'Multiple Thicknesses',
    applications: ['Roofing', 'Decorative', 'Industrial fabrication'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'ASTM B152, BS EN 1172',
      sizes: '1m × 2m, 1.2m × 2.4m × 0.5mm to 6mm',
      stock: '✓ In Stock',
      leadTime: '5-7 days'
    }
  },
  {
    id: 'coils',
    name: 'Copper Coils & Tubes',
    description: 'ACR grade copper coils for HVAC and refrigeration',
    icon: RefreshCw,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/a7j2hm18_images.jpeg',
    variants: 'Various OD/ID',
    applications: ['HVAC systems', 'Heat exchangers', 'Refrigeration'],
    specs: {
      purity: '99.9% (DHP/ACR Grade)',
      standards: 'ASTM B280, IS 6529',
      sizes: '1/4" to 7/8" OD × 15m to 30m coils',
      stock: '✓ In Stock',
      leadTime: '3-5 days'
    }
  },
  {
    id: 'pipes',
    name: 'Copper Pipes',
    description: 'Straight copper pipes for plumbing and industrial applications',
    icon: CircleDot,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/2lhm7dxy_manufacturing-process-copper-wires-cables_349938-315.jpg',
    variants: 'Standard & Custom',
    applications: ['Plumbing', 'Gas lines', 'Industrial piping'],
    specs: {
      purity: '99.9% (DHP Grade)',
      standards: 'ASTM B88, BS EN 1057',
      sizes: '15mm to 54mm OD × 5m to 6m lengths',
      stock: '✓ In Stock',
      leadTime: '3-5 days'
    }
  },
  {
    id: 'ingots',
    name: 'Copper Ingots & Cathodes',
    description: 'Pure copper ingots and cathodes for manufacturing',
    icon: Box,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/kuerv77w_Copper%20Ingots.jpeg',
    variants: 'Pure & Alloy Grades',
    applications: ['Manufacturing', 'Foundries', 'Fabrication'],
    specs: {
      purity: '99.95% to 99.99%',
      standards: 'ASTM B115, LME Grade A',
      sizes: '10kg to 125kg ingots, 99kg cathodes',
      stock: '✓ In Stock',
      leadTime: '5-7 days'
    }
  },
  {
    id: 'specialized',
    name: 'Enameled & Specialty Wires',
    description: 'Enameled, tin-coated, and specialty copper wires',
    icon: Zap,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/iyjnd3ck_Copper%20Enameled%20Wire.jpeg',
    variants: 'Multiple Coatings',
    applications: ['Motors', 'Transformers', 'Electronics'],
    specs: {
      purity: '99.95% + Enamel Coating',
      standards: 'IEC 60317, IS 13730',
      sizes: '0.2mm to 5mm, Grade 1/2/3',
      stock: 'Made to Order',
      leadTime: '10-14 days'
    }
  },
  {
    id: 'mother-tubes',
    name: 'Copper Mother Tubes',
    description: 'High-grade copper mother tubes for drawing operations',
    icon: Circle,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/zna08uyp_Copper%20Mother%20Tubes.jpeg',
    variants: '20+ Size Options',
    applications: ['Wire drawing', 'Tube manufacturing', 'Industrial processing'],
    specs: {
      purity: '99.9% (ETP/OF Grade)',
      standards: 'ASTM B75, Custom specs',
      sizes: '50mm to 300mm OD × Various wall',
      stock: 'Made to Order',
      leadTime: '14-21 days'
    }
  },
  {
    id: 'hex-rods',
    name: 'Copper Hex & Square Rods',
    description: 'Precision hexagonal and square copper rods for machining',
    icon: Hexagon,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/96ul81yb_Copper%20Hex%20and%20Square%20Rod.jpeg',
    variants: '15+ Sizes',
    applications: ['Precision parts', 'Fasteners', 'Machined components'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'ASTM B124, IS 614',
      sizes: '10mm to 50mm across flats × 3m',
      stock: '✓ In Stock',
      leadTime: '5-7 days'
    }
  },
  {
    id: 'square-rods',
    name: 'Earthing Strips & Rods',
    description: 'Copper strips and rods for electrical earthing systems',
    icon: Square,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/a12jgds2_BusBar.jpg',
    variants: 'Multiple Dimensions',
    applications: ['Electrical grounding', 'Lightning protection', 'Earthing systems'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'IS 3043, BS 4360',
      sizes: '25mm × 3mm to 50mm × 6mm × 3m',
      stock: '✓ In Stock',
      leadTime: '3-5 days'
    }
  },
  {
    id: 'sections',
    name: 'Copper Profiles & Sections',
    description: 'Custom copper profiles for specialized applications',
    icon: Grid,
    image: 'https://customer-assets.emergentagent.com/job_valor-copper/artifacts/wl08o3r8_Copper%20Profiles%20and%20Sections.jpeg',
    variants: 'Custom Shapes',
    applications: ['Architectural', 'Specialized manufacturing', 'Custom assemblies'],
    specs: {
      purity: '99.9% (ETP Grade)',
      standards: 'Custom to specification',
      sizes: 'As per customer drawing',
      stock: 'Made to Order',
      leadTime: '21-30 days'
    }
  },
];

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsConditions, setShowTermsConditions] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductModal, setShowProductModal] = useState(null);
  const [showRFQ, setShowRFQ] = useState(false);
  const [selectedProductForRFQ, setSelectedProductForRFQ] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [currentRoute, setCurrentRoute] = useState('home');
  const [footerContent, setFooterContent] = useState(null);

  useEffect(() => {
    // Check URL path
    const path = window.location.pathname;
    if (path === '/analytics') {
      setCurrentRoute('analytics');
    } else {
      setCurrentRoute('home');
    }
    
    fetchData();
    fetchFooterContent();
    
    // Scroll listener for navbar
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Auto-rotate testimonials
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, [testimonials.length]);

  // Load D&B script dynamically
  useEffect(() => {
    if (footerContent?.dnb_script_enabled && footerContent?.dnb_script_url) {
      const script = document.createElement('script');
      script.src = footerContent.dnb_script_url;
      script.type = 'text/javascript';
      script.language = 'JavaScript';
      script.async = true;
      
      const dnbWidget = document.getElementById('dnb-widget');
      if (dnbWidget) {
        dnbWidget.appendChild(script);
      }
      
      return () => {
        if (dnbWidget && script.parentNode === dnbWidget) {
          dnbWidget.removeChild(script);
        }
      };
    }
  }, [footerContent]);

  const fetchData = async () => {
    try {
      const [statsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/testimonials?featured_only=true`)
      ]);
      setStats({
        projects_completed: 2500,
        clients_served: 300,
        years_experience: 15,
        countries: 12,
        success_rate: 99
      });
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchFooterContent = async () => {
    try {
      const response = await axios.get(`${API}/content/footer`);
      setFooterContent(response.data);
    } catch (error) {
      console.error('Error fetching footer content:', error);
      // Set default content if fetch fails
      setFooterContent({
        company_description: "VALOR Dynamics is your trusted partner for high-quality copper products.",
        dnb_certificate_enabled: true,
        dnb_badge_image: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Dun_%26_Bradstreet_logo.svg/320px-Dun_%26_Bradstreet_logo.svg.png",
        dnb_certificate_url: "https://www.dnb.com/verify",
        company_name: "Valor Dynamics Pvt. Ltd.",
        cin: "U43219TG2020PTC140629",
        duns: "856831491"
      });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Thank you! We will get back to you within 24 hours.');
      setContactForm({ name: '', email: '', company: '', phone: '', service_interest: '', message: '' });
    } catch (error) {
      toast.error('Error submitting form. Please try again.');
    }
    setLoading(false);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success('Successfully subscribed! Check your email.');
      setNewsletterEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed!');
      } else {
        toast.error('Error subscribing. Please try again.');
      }
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const filteredProducts = PRODUCT_CATEGORIES.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.id === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="App">
      <Toaster position="top-right" richColors expand={true} />
      
      {/* Analytics Route */}
      {currentRoute === 'analytics' ? (
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading Analytics Dashboard...</p>
            </div>
          </div>
        }>
          <UltimateAnalyticsDashboard />
        </Suspense>
      ) : (
        <>
          <Suspense fallback={<div />}>
            <ZohoSalesIQ />
          </Suspense>
          
          {/* Sticky Contact Bar - Priority CX */}
          <StickyContactBar />
          
          {/* Floating Quick Quote Button - Priority CX */}
          <FloatingQuoteButton onClick={() => setShowQuickQuote(true)} />
          
          {/* Quick Quote Modal - Priority CX */}
          <QuickQuoteModal isOpen={showQuickQuote} onClose={() => setShowQuickQuote(false)} />
          
          {/* Premium Custom Cursor - Desktop only */}
          <div className="hidden lg:block">
            <Suspense fallback={<div />}>
              <CustomCursor />
            </Suspense>
          </div>
      
      {/* Social Proof Feed - Kahneman: Social Proof - Hidden on mobile */}
      {/* Disabled as per user request
      <div className="hidden lg:block">
        <SocialProofFeed />
      </div>
      */}
      
      {/* Exit Intent Modal - Kahneman: Scarcity + Reciprocity */}
      <Suspense fallback={<div />}>
        <ExitIntentModal />
      </Suspense>
      
      {/* Navigation - PHASE 2: Minimal & Sophisticated */}
      <nav className="fixed top-10 left-0 right-0 z-40 glass-nav transition-all duration-300" data-testid="navigation">
        <div className="w-full px-12 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Left */}
            <div className="flex items-center" style={{marginLeft: '0px'}}>
              <div 
                className="tracking-wider valor-logo cursor-pointer"
                data-testid="nav-logo"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{
                  fontSize: '3.5rem',
                  color: 'var(--valor-navy)'
                }}
              >
                VALOR
              </div>
            </div>
            
            {/* Desktop Menu - Center & Right */}
            <div className="hidden lg:flex items-center flex-1 justify-end space-x-6" style={{marginRight: '0px'}}>
              {/* Product Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => scrollToSection('products')}
                  placeholder="Search products..."
                  className="w-64 px-4 py-2 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                />
                <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
              
              {/* Navigation Links */}
              <div className="flex items-center space-x-6">
                <button onClick={() => setShowAboutUs(true)} className="text-gray-700 hover:text-[#0A2559] transition font-medium text-base">About</button>
                <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-[#0A2559] transition font-medium text-base">Products</button>
                <button onClick={() => scrollToSection('why-us')} className="text-gray-700 hover:text-[#0A2559] transition font-medium text-base">Why Us</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#0A2559] transition font-medium text-base">Contact</button>
              </div>
            </div>
            
            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-700" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-3 text-gray-700 hover:text-orange-500 font-medium min-h-[44px]">About</button>
              <button onClick={() => scrollToSection('products')} className="block w-full text-left py-3 text-gray-700 hover:text-orange-500 font-medium min-h-[44px]">Products</button>
              <button onClick={() => scrollToSection('why-us')} className="block w-full text-left py-3 text-gray-700 hover:text-orange-500 font-medium min-h-[44px]">Why Us</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-3 text-gray-700 hover:text-orange-500 font-medium min-h-[44px]">Contact</button>
              <a href="tel:+919966005098" className="block w-full text-left py-3 text-gray-700 hover:text-orange-500 font-medium flex items-center min-h-[44px]">
                <Phone size={18} className="mr-2" />
                +91 9966005098
              </a>
              <button onClick={() => { setMobileMenuOpen(false); setShowQuickQuote(true); }} className="bg-gradient-to-r from-orange-400 to-orange-500 text-white px-6 py-4 rounded-lg w-full font-semibold min-h-[48px]">Quick Quote</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - OPTIMIZED & STREAMLINED */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{minHeight: '100vh', paddingTop: '90px'}} data-testid="hero-section">
        {/* Full-Screen Background Image - OPTIMIZED */}
        <div className="absolute inset-0">
          <motion.img 
            src="https://images.unsplash.com/photo-1582489851864-4b4bddaf6a1b?w=1920&q=60&auto=format&fit=crop"
            alt="VALOR Manufacturing"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="1080"
            decoding="async"
            initial={{ scale: 1.05 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: "easeOut" }}
          />
          {/* Optimized dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        
        {/* Hero Content - Centered & Spacious */}
        <div className="relative w-full max-w-[95%] lg:max-w-[92%] xl:max-w-[90%] mx-auto px-4 text-center z-10">
          
          {/* Powerful Headline */}
          <motion.h1 
            className="text-white mb-8" 
            data-testid="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}
          >
            Delivering Copper.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-orange-400 to-orange-500">
              Powering Industries Worldwide
            </span>
          </motion.h1>
          
          {/* Compelling Subheadline - REFINED & CONCISE */}
          <motion.p 
            className="text-2xl mb-12 text-gray-200 max-w-5xl mx-auto leading-relaxed" 
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Quality copper products delivered fast. Simple ordering, reliable supply, competitive pricing for Indian businesses.
          </motion.p>
          
          {/* Clear & Prominent CTAs */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <button
              onClick={() => setShowQuickQuote(true)} 
              className="group bg-gradient-to-r from-orange-400 to-orange-500 text-white px-8 sm:px-14 py-4 sm:py-5 rounded-xl text-lg sm:text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center min-h-[56px]"
              data-testid="hero-cta-primary"
            >
              Get Quick Quote
              <ArrowRight className="ml-2 sm:ml-3 group-hover:translate-x-1 transition" size={20} />
            </button>
            <button
              onClick={() => window.location.href = 'tel:+919966005098'} 
              className="bg-white text-[#0A2559] px-8 sm:px-14 py-4 sm:py-5 rounded-xl text-lg sm:text-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all flex items-center justify-center min-h-[56px]"
              data-testid="hero-cta-secondary"
            >
              <Phone className="mr-2 sm:mr-3" size={20} />
              Call Now
            </button>
          </motion.div>
        </div>
      </section>

      {/* About Section - CONDENSED & ELEGANT */}
      <section id="about" className="py-16 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          
          {/* Section Header - Compact */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block mb-3">
              <span className="text-sm font-bold text-orange-500 tracking-[0.2em] uppercase">About Valor Dynamics</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Your Trusted Copper Supplier
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              As trusted OEM suppliers, we connect businesses with quality copper products. Fast delivery, competitive pricing, reliable supply.
            </p>
          </motion.div>

          {/* Mission & Vision - Compact Side-by-Side */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            
            {/* Mission Card - Compact */}
            <motion.div 
              className="group bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:border-orange-400 transition-all duration-300"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Our Mission</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Deliver quality copper products on time, every time. Simple ordering, transparent pricing, reliable supply for growing businesses.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Vision Card - Compact */}
            <motion.div 
              className="group bg-gradient-to-br from-blue-900 to-slate-800 rounded-2xl shadow-lg p-6 border border-blue-800 hover:border-blue-600 transition-all duration-300"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Our Vision</h3>
                  <p className="text-sm text-gray-100 leading-relaxed">
                    Be India's most reliable copper supplier—where quality meets speed, and every order strengthens long-term partnerships.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <TrustSignals />

      {/* Products Section - PHASE 3: Apple-Inspired Grid */}
      <section id="products" className="py-12 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{color: 'var(--valor-charcoal)'}}>
              Copper Products We Supply
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              12 product categories sourced from trusted manufacturers - ready to ship across India
            </p>
          </motion.div>
          
          {/* Search and Filter */}
          <div className="mb-12 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input 
                  type="text"
                  placeholder="Search by product name (e.g., Copper Rods, Bus Bars)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  data-testid="product-search"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className={`px-6 py-3 rounded-xl font-semibold whitespace-nowrap transition ${
                    selectedCategory === 'all' 
                      ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Products
                </button>
                {['flats', 'rods', 'sheets', 'coils'].map(cat => (
                  <button 
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-6 py-3 rounded-xl font-semibold whitespace-nowrap transition capitalize ${
                      selectedCategory === cat
                        ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Product Grid - PHASE 3: Apple-Inspired 3-Column */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div 
                key={product.id} 
                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-orange-500 transition-all duration-300"
                data-testid={`product-${product.id}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ 
                  y: -12,
                  boxShadow: "0 24px 48px rgba(249, 115, 22, 0.15)"
                }}
              >
                {/* Image Container */}
                <div className="relative overflow-hidden h-72 bg-gray-50">
                  <motion.img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                    decoding="async"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                  {/* Gradient Overlay on Hover */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-transparent to-transparent"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="absolute bottom-6 left-6 right-6">
                      <motion.button 
                        onClick={() => setShowProductModal(product)}
                        className="w-full bg-white text-orange-500 py-3 rounded-xl font-semibold flex items-center justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        whileHover={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Eye size={20} className="mr-2" />
                        View Details
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
                
                {/* Content */}
                <div className="p-8">
                  <div className="mb-3">
                    <span className="text-sm font-bold text-orange-500 bg-orange-50 px-3 py-1.5 rounded-full">
                      {product.variants}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-500 transition" style={{color: 'var(--valor-charcoal)'}}>
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-5 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="space-y-2 mb-5">
                    <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Key Applications</div>
                    {product.applications.slice(0, 3).map((app, i) => (
                      <div key={i} className="flex items-center text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></div>
                        {app}
                      </div>
                    ))}
                  </div>
                  
                  {/* Tiered CTA Strategy based on product type */}
                  <div className="space-y-2">
                    {/* Primary CTA - Based on product complexity */}
                    {(product.specs?.stock === 'Made to Order' || (product.specs?.leadTime && (product.specs.leadTime.includes('14') || product.specs.leadTime.includes('21') || product.specs.leadTime.includes('30')))) ? (
                      // Specialized products - "Talk to Expert"
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToSection('contact');
                          toast.success('Our product expert will help you with custom requirements!');
                        }}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3.5 rounded-lg font-semibold text-sm transition flex items-center justify-center shadow-lg hover:shadow-xl min-h-[48px]"
                      >
                        <Users size={16} className="mr-2" />
                        Talk to Product Expert
                      </button>
                    ) : (
                      // Commodity products - "Get Custom Quote"
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowQuickQuote(true);
                          toast.success(`Quote request for ${product.name} opened!`);
                        }}
                        className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white py-3.5 rounded-lg font-semibold text-sm transition flex items-center justify-center shadow-lg hover:shadow-xl min-h-[48px]"
                      >
                        <Package size={16} className="mr-2" />
                        Get Custom Quote
                      </button>
                    )}
                    
                    {/* Secondary CTA - Email specs (always available) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const email = prompt('Enter your email to receive full specifications:');
                        if (email && email.includes('@')) {
                          toast.success('Spec sheet will be sent to ' + email);
                          // Here you would normally send to backend
                        } else if (email) {
                          toast.error('Please enter a valid email address');
                        }
                      }}
                      className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-3 rounded-lg font-medium text-xs transition flex items-center justify-center border border-gray-200 min-h-[44px]"
                    >
                      <Download size={14} className="mr-1.5" />
                      📄 Email Full Specs to Me
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <Package className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No products found matching your criteria</p>
            </div>
          )}
          
          {/* CTA - Enhanced with Motion */}
          <motion.div 
            className="mt-16 text-center bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl p-12 text-white shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl font-bold mb-4">Can't Find What You Need?</h3>
            <p className="text-xl text-orange-50 mb-8 max-w-2xl mx-auto">
              We source copper products to your exact specifications. Get a custom quote fast.
            </p>
            <MagneticButton
              onClick={() => setShowQuickQuote(true)}
              className="bg-white text-orange-500 px-10 py-4 rounded-xl font-bold hover:shadow-2xl transition inline-flex items-center text-lg"
            >
              Get Custom Quote Now <ArrowRight className="ml-2" size={22} />
            </MagneticButton>
          </motion.div>
        </div>
      </section>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowProductModal(null)}>
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={showProductModal.image} alt={showProductModal.name} className="w-full h-64 object-cover rounded-t-3xl" />
              <button 
                onClick={() => setShowProductModal(null)}
                className="absolute top-4 right-4 bg-white rounded-xl p-2 shadow-lg hover:bg-gray-100 transition"
              >
                <X size={24} />
              </button>
              <div className="absolute bottom-4 left-4">
                {showProductModal.icon && React.createElement(showProductModal.icon, { className: "w-16 h-16 text-white drop-shadow-lg", strokeWidth: 1.5 })}
              </div>
            </div>
            <div className="p-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{showProductModal.name}</h2>
              <p className="text-xl text-gray-600 mb-8">{showProductModal.description}</p>
              
              {/* Technical Specifications */}
              {showProductModal.specs && (
                <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl p-6 mb-8 border-2 border-orange-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Award className="w-6 h-6 text-orange-500 mr-2" />
                    Technical Specifications
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Purity</div>
                      <div className="text-lg font-bold text-gray-900">{showProductModal.specs.purity}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Standards</div>
                      <div className="text-lg font-bold text-gray-900">{showProductModal.specs.standards}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Available Sizes</div>
                      <div className="text-lg font-bold text-gray-900">{showProductModal.specs.sizes}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Stock Status</div>
                      <div className="text-lg font-bold text-green-600">{showProductModal.specs.stock}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Lead Time</div>
                      <div className="text-lg font-bold text-blue-600">{showProductModal.specs.leadTime}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-sm text-gray-500 font-semibold mb-1">Variants</div>
                      <div className="text-lg font-bold text-gray-900">{showProductModal.variants}</div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Zap className="w-6 h-6 text-orange-500 mr-2" />
                    Applications
                  </h3>
                  <ul className="space-y-3">
                    {showProductModal.applications.map((app, i) => (
                      <li key={i} className="flex items-center text-gray-700 bg-gray-50 p-3 rounded-lg">
                        <CheckCircle size={20} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="font-medium">{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-2xl p-6 border-2 border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Why Choose VALOR?</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Test certificates included with every order</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Pan-India delivery in 3-5 days</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>No minimum order quantity</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Flexible credit terms available</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                {/* Primary CTA - Tiered based on product type */}
                {(showProductModal.specs?.stock === 'Made to Order' || (showProductModal.specs?.leadTime && (showProductModal.specs.leadTime.includes('14') || showProductModal.specs.leadTime.includes('21') || showProductModal.specs.leadTime.includes('30')))) ? (
                  // Specialized product - Talk to Expert
                  <button 
                    onClick={() => {
                      setShowProductModal(null);
                      scrollToSection('contact');
                      toast.success('Our expert will help you with custom specifications!');
                    }}
                    className="flex-1 min-w-[200px] bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-xl font-bold hover:shadow-xl transition flex items-center justify-center"
                  >
                    <Users size={20} className="mr-2" />
                    Talk to Product Expert
                  </button>
                ) : (
                  // Commodity product - Get Quote
                  <button 
                    onClick={() => {
                      setShowProductModal(null);
                      setShowQuickQuote(true);
                    }}
                    className="flex-1 min-w-[200px] bg-gradient-to-r from-orange-400 to-orange-500 text-white py-4 rounded-xl font-bold hover:shadow-xl transition flex items-center justify-center"
                  >
                    <MessageSquare size={20} className="mr-2" />
                    Get Custom Quote
                  </button>
                )}
                
                {/* Secondary CTA - Email Specs */}
                <button 
                  onClick={() => {
                    const email = prompt('Enter your email to receive full specifications for ' + showProductModal.name + ':');
                    if (email && email.includes('@')) {
                      toast.success('Full spec sheet will be sent to ' + email);
                      // Here you would normally send to backend
                    } else if (email) {
                      toast.error('Please enter a valid email address');
                    }
                  }}
                  className="flex-1 min-w-[200px] bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition flex items-center justify-center border-2 border-gray-300"
                >
                  <Download size={20} className="mr-2" />
                  📧 Email Specs
                </button>
                
                <button 
                  onClick={() => setShowProductModal(null)}
                  className="px-8 bg-white text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-50 transition border-2 border-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Us + Core Values Section - OPTIMIZED TO 5 PILLARS */}
      <section id="why-us" className="py-12 bg-gradient-to-br from-[#051a3d] via-[#0A2559] to-[#1e3a8a] text-white relative overflow-hidden" data-testid="why-us-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-grid-pattern"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="text-sm font-semibold text-orange-400 mb-3 tracking-wider uppercase"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              The VALOR Advantage
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Partners Choose Us
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Five core principles that drive every decision and partnership
            </p>
          </motion.div>
          
          {/* 5 Pillars Grid - OPTIMIZED & CONDENSED */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: <Shield className="w-10 h-10" />,
                title: 'Supply Certainty',
                description: 'Material availability guaranteed when you need it. No surprises, no delays—just reliable sourcing that keeps your production running.',
                gradient: 'from-orange-400 to-orange-500'
              },
              {
                icon: <Zap className="w-10 h-10" />,
                title: 'Speed & Responsiveness',
                description: 'Quote to delivery in record time. Emergency orders? We respond in hours. Quick decisions, faster delivery—time matters.',
                gradient: 'from-blue-600 to-blue-700'
              },
              {
                icon: <Handshake className="w-10 h-10" />,
                title: 'Partnership-First Approach',
                description: 'We do not just sell copper—we solve problems and build lasting relationships. Your success is our priority. Your challenges become ours.',
                gradient: 'from-orange-400 to-orange-500'
              },
              {
                icon: <Target className="w-10 h-10" />,
                title: 'Transparent Pricing',
                description: 'No hidden costs, no games. Clear quotes, honest communication. Market-aligned rates with volume benefits—you know exactly what you pay.',
                gradient: 'from-blue-600 to-blue-700'
              },
              {
                icon: <Lightbulb className="w-10 h-10" />,
                title: 'Continuous Improvement',
                description: 'Always learning, always improving. Yesterday\'s standard is today\'s starting point. We evolve with your needs.',
                gradient: 'from-orange-400 to-orange-500'
              }
            ].map((pillar, index) => (
              <motion.div 
                key={index}
                className={`group relative ${index === 4 ? 'md:col-span-2 lg:col-span-1 lg:col-start-2' : ''}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300 h-full">
                  {/* Icon */}
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-br ${pillar.gradient} rounded-2xl flex items-center justify-center mb-5 text-white`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {pillar.icon}
                  </motion.div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold mb-3">{pillar.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">
                    {pillar.description}
                  </p>
                  
                  {/* Hover Effect */}
                  <motion.div 
                    className="absolute inset-0 rounded-3xl bg-gradient-to-br from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Simon Sinek Quote - Bottom */}
          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="max-w-3xl mx-auto bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-orange-400/10 rounded-full blur-3xl"></div>
              <div className="relative z-10 text-center">
                <p className="text-xl md:text-2xl font-light italic leading-relaxed mb-3">
                  "People do not buy what you do; they buy why you do it."
                </p>
                <p className="text-orange-400 font-semibold text-sm">— Simon Sinek</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Section - Moved Up for Better Social Proof */}
      <CaseStudies />

      {/* Certifications + Download Center - STREAMLINED (Redundancy Removed) */}
      <section className="py-16 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-full mb-4">
              <Award className="w-5 h-5 text-orange-500" />
              <span className="text-sm font-bold text-orange-500 tracking-wider uppercase">
                Certifications & Resources
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{color: 'var(--valor-charcoal)'}}>
              Certificates & Downloads
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Access all quality certifications, compliance documents, and company resources
            </p>
          </motion.div>

          {/* Streamlined: Direct Download Cards (Removed Redundant Overview) */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: "ISO 9001:2015 Certificate",
                  description: "Quality Management System Certification",
                  icon: Award,
                  fileUrl: "https://customer-assets.emergentagent.com/job_valor-copper/artifacts/t25h2osy_VDPL%20ISO%209001.pdf",
                  fileName: "VDPL_ISO_9001_Certificate.pdf",
                  color: 'from-blue-600 to-blue-700'
                },
                {
                  title: "ISO 45001:2018 Certificate",
                  description: "Health & Safety Management Certification",
                  icon: Shield,
                  fileUrl: "https://customer-assets.emergentagent.com/job_valor-copper/artifacts/prkv2xc5_VDPL%20ISO%2045001.pdf",
                  fileName: "VDPL_ISO_45001_Certificate.pdf",
                  color: 'from-orange-400 to-orange-500'
                },
                {
                  title: "D&B Certificate",
                  description: "Dun & Bradstreet Verified Supplier",
                  icon: FileCheck,
                  fileUrl: "https://customer-assets.emergentagent.com/job_valor-copper/artifacts/g23aa7dl_D%26B%20Certificate.pdf",
                  fileName: "VDPL_DnB_Certificate.pdf",
                  color: 'from-blue-600 to-blue-700'
                },
                {
                  title: "Company Information Sheet",
                  description: "Regulatory & banking details",
                  icon: Building2,
                  fileUrl: "https://customer-assets.emergentagent.com/job_valor-copper/artifacts/qsx7gx48_VDPL_CIS%20One%20Page.pdf",
                  fileName: "VDPL_Company_Information.pdf",
                  color: 'from-orange-400 to-orange-500'
                },
                {
                  title: "Product Catalog 2025",
                  description: "Complete copper products range",
                  icon: Package,
                  fileUrl: "#",
                  fileName: "VALOR_Product_Catalog_2025.pdf",
                  color: 'from-green-600 to-green-700'
                },
                {
                  title: "Technical Specifications",
                  description: "ASTM & BIS standards details",
                  icon: FileCheck,
                  fileUrl: "#",
                  fileName: "VALOR_Technical_Specs.pdf",
                  color: 'from-purple-600 to-purple-700'
                }
              ].map((cert, index) => (
                <motion.div
                  key={index}
                  className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-orange-500 relative overflow-hidden cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  whileHover={{ y: -8 }}
                  onClick={() => {
                    if (cert.fileUrl !== "#") {
                      const link = document.createElement('a');
                      link.href = cert.fileUrl;
                      link.download = cert.fileName;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } else {
                      toast.info('Coming soon! Contact us for this document.');
                    }
                  }}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${cert.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <cert.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {cert.description}
                  </p>
                  <div className="flex items-center text-orange-500 font-semibold text-sm group-hover:gap-2 transition-all">
                    <Download className="w-4 h-4 mr-2" />
                    <span>Download PDF</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom: Regulatory Information Banner */}
          <motion.div 
            className="bg-gradient-to-r from-blue-900 to-slate-800 rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold mb-6 text-center">Regulatory Compliance</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-sm">
              <div>
                <div className="font-semibold text-orange-400 mb-1">CIN</div>
                <div className="text-gray-200 text-xs md:text-sm break-all">U43219TG2020PTC140629</div>
              </div>
              <div>
                <div className="font-semibold text-orange-400 mb-1">DUNS</div>
                <div className="text-gray-200">856831491</div>
              </div>
              <div>
                <div className="font-semibold text-orange-400 mb-1">UDYAM</div>
                <div className="text-gray-200 text-xs md:text-sm break-all">TS-02-0089358</div>
              </div>
              <div>
                <div className="font-semibold text-orange-400 mb-1">Verified</div>
                <div className="text-gray-200">D&B Certified</div>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-xs md:text-sm text-gray-300">
                ✓ ISO 9001:2015  |  ✓ ISO 45001:2018  |  ✓ All India Delivery
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - Early Friction Reduction */}
      <FAQSection />

      {/* Testimonials - REMOVED: Awaiting verified client testimonials */}
      {/* Will be added once we have written permissions from clients */}

      {/* Contact Section */}
      <section id="contact" className="py-12 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title mb-3">Contact Us</h2>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get in touch for quotes, inquiries, or to discuss your copper product needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              {/* Response Guarantee */}
              <ResponseBadge type="contact" />
              
              <form onSubmit={handleContactSubmit} className="space-y-6 bg-white rounded-2xl shadow-xl p-8 border border-gray-100" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Full Name *</label>
                  <input 
                    type="text" 
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    placeholder="John Doe"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    placeholder="john@company.com"
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Company</label>
                    <input 
                      type="text" 
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      placeholder="Company Name"
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Phone</label>
                    <input 
                      type="tel" 
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      placeholder="+91 9966005098"
                      data-testid="contact-input-phone"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Product Interest</label>
                  <select 
                    value={contactForm.service_interest}
                    onChange={(e) => setContactForm({...contactForm, service_interest: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    data-testid="contact-input-service"
                  >
                    <option value="">Select a product</option>
                    <option value="Copper Flats">Copper Flats</option>
                    <option value="Copper Rods & Wires">Copper Rods & Wires</option>
                    <option value="Copper Strips">Copper Strips</option>
                    <option value="Copper Sheets">Copper Sheets</option>
                    <option value="Copper Coils">Copper Coils</option>
                    <option value="Copper Pipes">Copper Pipes</option>
                    <option value="Copper Ingots">Copper Ingots</option>
                    <option value="Specialized Wires">Specialized Wires</option>
                    <option value="Custom Requirements">Custom Requirements</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Message *</label>
                  <textarea 
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    placeholder="Tell us about your requirements..."
                    data-testid="contact-input-message"
                  />
                </div>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-400 to-orange-500 text-white px-8 py-4 rounded-lg text-lg font-bold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center"
                  data-testid="contact-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Inquiry'} <Send className="ml-2" size={20} />
                </button>
              </form>
            </div>
            
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-blue-900 to-slate-800 rounded-2xl shadow-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Get In Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-start" data-testid="contact-info-email">
                    <div className="w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Email</div>
                      <a href="mailto:avc@govalor.co" className="text-gray-300 hover:text-orange-500 transition">avc@govalor.co</a>
                    </div>
                  </div>
                  <div className="flex items-start" data-testid="contact-info-phone">
                    <div className="w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Phone</div>
                      <a href="tel:+919966005098" className="text-gray-300 hover:text-orange-500 transition">+91 9966005098</a>
                    </div>
                  </div>
                  <div className="flex items-start" data-testid="contact-info-address">
                    <div className="w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Registered Office</div>
                      <a 
                        href="https://maps.app.goo.gl/m2WrV75rSBZPRtyK7" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-300 hover:text-orange-500 transition-colors inline-flex items-start group"
                      >
                        <div>
                          Valor Dynamics Private Limited.<br/>
                          Plot No. 2, 1282/A, Pitruchaya,<br/>
                          Road No. 64, Hyderabad,<br/>
                          Telangana, India
                        </div>
                        <MapPin className="w-4 h-4 ml-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                      <a 
                        href="https://maps.app.goo.gl/m2WrV75rSBZPRtyK7" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-orange-500 hover:text-orange-400 mt-2 font-semibold"
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-orange-400 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">Website</div>
                      <a href="https://www.govalor.co" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-orange-500 transition">www.govalor.co</a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Hours</h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex justify-between">
                    <span className="font-semibold">Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Saturday</span>
                    <span>9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Sunday</span>
                    <span className="text-gray-400">Closed</span>
                  </div>
                </div>
              </div>
              
              {/* Google Map Embed */}
              <div className="overflow-hidden rounded-2xl shadow-2xl border-4 border-white">
                <a 
                  href="https://maps.app.goo.gl/m2WrV75rSBZPRtyK7" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block relative group"
                >
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.588!2d78.4486!3d17.4366!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9a1e8b5c9d5b%3A0x5e5e5e5e5e5e5e5e!2sPlot%20No.%202%2C%201282%2FA%2C%20Pitruchaya%2C%20Road%20No.%2064%2C%20Hyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="320"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="VALOR Office Location - Hyderabad"
                    className="w-full"
                  ></iframe>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
                    <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-xl opacity-0 group-hover:opacity-100 transition-all transform translate-y-4 group-hover:translate-y-0 flex items-center gap-2 font-semibold text-gray-900 shadow-lg pointer-events-auto">
                      <MapPin className="w-5 h-5 text-orange-500" />
                      Open in Google Maps
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - APPLE-INSPIRED: Minimal, Sophisticated & Clean */}
      <footer className="bg-white border-t border-gray-200" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-12">
            <div className="grid md:grid-cols-5 gap-12">
              {/* Column 1: Company Info */}
              <div className="md:col-span-2">
                <div 
                  className="tracking-wider mb-6 valor-logo"
                  data-testid="footer-wordmark-logo"
                  style={{
                    fontSize: '2.5rem',
                    color: 'var(--valor-navy)'
                  }}
                >
                  VALOR
                </div>
                <p className="text-base text-gray-600 mb-6 max-w-sm leading-relaxed">
                  {footerContent?.company_description || "Trusted copper supplier for Indian businesses. Fast delivery, competitive pricing, reliable supply."}
                </p>
                
                {/* D&B Certificate Badge & Script */}
                {footerContent?.dnb_certificate_enabled && (
                  <div className="mb-6">
                    {/* D&B Script Widget Container */}
                    {footerContent?.dnb_script_enabled && footerContent?.dnb_script_url && (
                      <div id="dnb-widget" className="mb-4"></div>
                    )}
                    
                    {/* D&B Badge Image (fallback) */}
                    {footerContent?.dnb_badge_image && (
                      <a 
                        href={footerContent.dnb_certificate_url || "https://www.dnb.com/verify"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block hover:opacity-80 transition"
                      >
                        <img 
                          src={footerContent.dnb_badge_image}
                          alt={footerContent.dnb_badge_alt || "D&B Verified Business"}
                          className="h-16 w-auto"
                        />
                      </a>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      DUNS: {footerContent?.duns || "856831491"}
                    </p>
                  </div>
                )}
                
                <div className="mb-8 text-sm text-gray-600">
                  <div className="flex items-start mb-3">
                    <MapPin className="w-4 h-4 text-orange-500 mt-1 mr-2 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">Registered Office</div>
                      <a 
                        href="https://maps.app.goo.gl/m2WrV75rSBZPRtyK7" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-orange-500 transition-colors"
                      >
                        Valor Dynamics Private Limited.<br/>
                        Plot No. 2, 1282/A, Pitruchaya,<br/>
                        Road No. 64, Hyderabad,<br/>
                        Telangana, India
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center mb-2">
                    <Phone className="w-4 h-4 text-orange-500 mr-2" />
                    <a href="tel:+919966005098" className="hover:text-orange-500 transition">+91 9966005098</a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-orange-500 mr-2" />
                    <a href="mailto:avc@govalor.co" className="hover:text-orange-500 transition">avc@govalor.co</a>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <a href="#" className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-orange-400 hover:text-white transition-all duration-300" data-testid="social-linkedin">
                    <Linkedin size={20} />
                  </a>
                  <a href="#" className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-orange-400 hover:text-white transition-all duration-300" data-testid="social-twitter">
                    <Twitter size={20} />
                  </a>
                  <a href="#" className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-orange-400 hover:text-white transition-all duration-300" data-testid="social-facebook">
                    <Facebook size={20} />
                  </a>
                  <a href="#" className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-orange-400 hover:text-white transition-all duration-300" data-testid="social-instagram">
                    <Instagram size={20} />
                  </a>
                </div>
              </div>
              
              {/* Column 2: Company */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">Company</h4>
                <ul className="space-y-4">
                  <li><button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-orange-500 transition text-sm">About Us</button></li>
                  <li><button onClick={() => scrollToSection('why-us')} className="text-gray-700 hover:text-orange-500 transition text-sm">Why VALOR</button></li>
                  <li><button onClick={() => window.open('https://customer-assets.emergentagent.com/job_valor-copper/certifications', '_blank')} className="text-gray-700 hover:text-orange-500 transition text-sm">Certifications</button></li>
                  <li><button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-orange-500 transition text-sm">Testimonials</button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-orange-500 transition text-sm">Contact</button></li>
                </ul>
              </div>
              
              {/* Column 3: Products */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">Products</h4>
                <ul className="space-y-4 text-sm text-gray-700">
                  <li><button onClick={() => { setProductFilter('flats'); scrollToSection('products'); }} className="hover:text-orange-500 transition text-left">Copper Flats</button></li>
                  <li><button onClick={() => { setProductFilter('rods'); scrollToSection('products'); }} className="hover:text-orange-500 transition text-left">Copper Rods</button></li>
                  <li><button onClick={() => { setProductFilter('sheets'); scrollToSection('products'); }} className="hover:text-orange-500 transition text-left">Copper Sheets</button></li>
                  <li><button onClick={() => { setProductFilter('coils'); scrollToSection('products'); }} className="hover:text-orange-500 transition text-left">Copper Coils</button></li>
                  <li><button onClick={() => { setProductFilter('ingots'); scrollToSection('products'); }} className="hover:text-orange-500 transition text-left">Copper Ingots</button></li>
                </ul>
              </div>
              
              {/* Column 4: Stay Connected */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">Copper Price Alerts</h4>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  <strong className="text-gray-900">Get price alerts & exclusive deals.</strong><br/>
                  Be first to know when prices drop.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-3" data-testid="newsletter-form">
                  <input 
                    type="email" 
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Your business email"
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    data-testid="newsletter-input"
                  />
                  <button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-4 py-3 rounded-lg transition font-semibold text-sm flex items-center justify-center"
                    data-testid="newsletter-submit"
                  >
                    Get Alerts <ArrowRight size={16} className="ml-2" />
                  </button>
                </form>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar - Apple Style */}
          <div className="border-t border-gray-200 py-6 md:py-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
              <div className="text-sm text-gray-500 text-center md:text-left">
                <div>© {new Date().getFullYear()} {footerContent?.company_name || "Valor Dynamics Pvt. Ltd."}. All rights reserved.</div>
                <div className="mt-2 text-xs">
                  CIN: {footerContent?.cin || "U43219TG2020PTC140629"} | DUNS: {footerContent?.duns || "856831491"}
                </div>
              </div>
              <div className="flex gap-6 md:gap-10 text-sm text-gray-600">
                <button onClick={() => setShowPrivacyPolicy(true)} className="hover:text-gray-900 transition">Privacy Policy</button>
                <button onClick={() => setShowTermsConditions(true)} className="hover:text-gray-900 transition">Terms & Conditions</button>
                <a href="mailto:avc@govalor.co" className="hover:text-gray-900 transition">Contact Legal</a>
                <a href="/analytics" className="hover:text-orange-500 transition opacity-50 hover:opacity-100">Analytics</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Legal Pages Modals */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={() => setShowPrivacyPolicy(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-white">Loading...</div></div>}>
              <PrivacyPolicy />
            </Suspense>
          </div>
        </div>
      )}
      
      {showTermsConditions && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={() => setShowTermsConditions(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-white">Loading...</div></div>}>
              <TermsConditions />
            </Suspense>
          </div>
        </div>
      )}
      
      {showAboutUs && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={() => setShowAboutUs(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-white">Loading...</div></div>}>
              <AboutUs onClose={() => setShowAboutUs(false)} />
            </Suspense>
          </div>
        </div>
      )}
      
      {showContactUs && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={() => setShowContactUs(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-white">Loading...</div></div>}>
              <ContactUs />
            </Suspense>
          </div>
        </div>
      )}

      {/* RFQ System Modal */}
      <Suspense fallback={<div />}>
        <RFQSystem 
          isOpen={showRFQ}
          onClose={() => setShowRFQ(false)}
          selectedProduct={selectedProductForRFQ}
        />
      </Suspense>
      
      {/* Scroll to Top Button */}
      {scrolled && (
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 left-8 bg-gradient-to-r from-blue-900 to-slate-800 text-white w-12 h-12 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-110 transition flex items-center justify-center z-40"
          aria-label="Scroll to top"
        >
          <ChevronDown className="rotate-180" size={24} />
        </button>
      )}
      </>
      )}
    </div>
  );
}

export default App;